import type { AxiosPromise } from 'axios';
// eslint-disable-next-line no-restricted-imports
import { toFetcher } from '@engage/data-fetch-v2/fetchers/toFetcher';
// eslint-disable-next-line no-restricted-imports
import { unwrap } from '@engage/data-fetch-v2/lib/unwrap';
import { clubPelotonApi } from '../singletons';

type Params = { userId?: string };

/**
 * A single tier segment on the progress bar (e.g. "Gold I" -> "Gold II").
 * Field names are camelized from the club-peloton REST response by the shared
 * `camelizeKeys` response transform on the axios client.
 */
export type SegmentTierMaxPoints = {
  label: string;
  minPoints: number;
  maxPoints: number;
  nextLevelName?: string;
};

export type ClubPelotonProgressBar = {
  badgeLevelName?: string;
  score?: number;
  tierBasePoints?: number;
  tierMaxPoints?: number;
  segmentTiersMaxPoints?: SegmentTierMaxPoints[];
};

/**
 * The member's boost from the v2 score payload, `null` when they have none. Every string is
 * localized server-side, so render it as-is. `status` is "active" | "completed" | "expired".
 */
export type ClubPelotonBoost = {
  boostId: string;
  status: string;
  expiresAt?: string | null;
  title?: string;
  /** Second line, set only once the boost is over (completed / expired). */
  subtitle?: string;
  ribbonTitle?: string;
  /** Pre-phrased window, e.g. "6 days left" / "Bonus earned". */
  expirationLabel?: string;
  /** Dark-mode boost sprite sheet (single row, 57 frames) for the details page. */
  detailAnimationUrl?: string;
  themeAnimationUrl?: string;
  /** Legacy name for `themeAnimationUrl`. */
  animationUrl?: string;
};

/**
 * Presentation-ready score payload from club-peloton `GET /v2/user/{userID}/score`.
 * The service returns snake_case; the client's default response transforms
 * (`camelizeKeys`) convert it to the camelCase shape below.
 */
export type ClubPelotonScoreResponse = {
  badgeLevelName?: string;
  totalEarnedPoints?: number;
  staticBadgeUrl?: string;
  animatedBadgeUrl?: string;
  /** Per-level animated hero (S3 mp4), e.g. `.../hero_<slug>_dark.mp4`. */
  headerVideoUrl?: string;
  levelSlug?: string;
  progressBar?: ClubPelotonProgressBar;
  webviewUrl?: string;
  faqUrl?: string;
  pointsHistoryWebviewUrl?: string;
  boost?: ClubPelotonBoost | null;
};

function getScore(
  api: typeof clubPelotonApi,
  userId?: string,
): AxiosPromise<ClubPelotonScoreResponse> {
  return api.axios.get(`v2/user/${userId}/score`);
}

export const toClubPelotonFetchers = (api: typeof clubPelotonApi = clubPelotonApi) => ({
  // `toFetcher` defers execution (null cache key) automatically when `userId` is
  // absent, so callers can pass `{ userId: undefined }` before auth resolves.
  GetScore: toFetcher(
    (params: Params) => ['ClubPelotonScore', params.userId],
    (params: Params) => unwrap(() => getScore(api, params.userId)),
  ),
});
